/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { GenericButton } from "@ui-components/Button/GenericButton";
import GenericInput from "@ui-components/Input/GenericInput";
import { useAppSelector } from "@redux/hooks";
import { useAxiosAuth0 } from "@axios-config/useAxiosAuth0";
import { listMembers, addMembers, removeMember } from "../requests/request";
import { listPlatformUsersForTenant } from "../requests/notificationsPickerDataSource";
import { GroupMember, NotificationGroup, AddMemberInput } from "../types";
import type { User } from "settings/account/components/TagsManager/types";
import {
  GenericTable,
  type GenericTableColumn,
} from "@ui-components/Table/GenericTable";

const RECIPIENTS_EXTERNAL_TAB = "external";

/** Persisted/API members lose picker scope → single platform bucket. */
const RECIPIENTS_PLATFORM_BUCKET = "tenant:0";

/**
 * Recipients table caps at ~5 visible body rows before the user must scroll.
 * `GenericTable` cells use `px-4 py-2` + `text-sm`, ≈ 40px header + 5 × 40px rows.
 */
const RECIPIENTS_TABLE_MAX_ROWS = 5;
const RECIPIENTS_TABLE_ROW_HEIGHT_PX = 40;
const RECIPIENTS_TABLE_HEADER_HEIGHT_PX = 40;
const RECIPIENTS_TABLE_MAX_BODY_HEIGHT = `${
  RECIPIENTS_TABLE_HEADER_HEIGHT_PX +
  RECIPIENTS_TABLE_MAX_ROWS * RECIPIENTS_TABLE_ROW_HEIGHT_PX
}px`;

function recipientTabKeyFromDraftMember(m: AddMemberInput): string {
  if (m.type === "email") return RECIPIENTS_EXTERNAL_TAB;
  return `tenant:${m.pickerTenantId ?? 0}`;
}

function recipientTabKeyPersisted(m: GroupMember): string {
  if (m.memberType === "email") return RECIPIENTS_EXTERNAL_TAB;
  return RECIPIENTS_PLATFORM_BUCKET;
}

function sortRecipientTabKeys(keys: string[]): string[] {
  return [...keys].sort((a, b) => {
    if (
      a === RECIPIENTS_EXTERNAL_TAB &&
      b !== RECIPIENTS_EXTERNAL_TAB
    )
      return 1;
    if (
      b === RECIPIENTS_EXTERNAL_TAB &&
      a !== RECIPIENTS_EXTERNAL_TAB
    )
      return -1;
    const na = a.startsWith("tenant:")
      ? Number(a.slice("tenant:".length))
      : 0;
    const nb = b.startsWith("tenant:")
      ? Number(b.slice("tenant:".length))
      : 0;
    return na - nb;
  });
}

function draftMemberKey(m: AddMemberInput): string {
  if (m.type === "user") return `user:${m.auth0Id}`;
  return `email:${m.email.toLowerCase()}`;
}

function mapDraftToGroupMembers(draft: AddMemberInput[]): GroupMember[] {
  return draft.map((m) => ({
    id: `draft::${draftMemberKey(m)}`,
    groupId: "",
    memberType: m.type,
    auth0Id: m.type === "user" ? m.auth0Id : null,
    email: m.email,
    displayName: m.displayName ?? null,
    displayLabel:
      (m.type === "user" ? m.displayName || m.email : m.displayName || m.email) ??
      m.email,
    createdAt: "",
  }));
}

interface Props {
  /** Null during the create wizard — recipients stay local until the parent saves the group. */
  group: NotificationGroup | null;
  draftMembers?: AddMemberInput[];
  onDraftMembersChange?: (members: AddMemberInput[]) => void;
  readOnly?: boolean;
}

const GroupMembersSection: React.FC<Props> = ({
  group,
  draftMembers = [],
  onDraftMembersChange,
  readOnly = false,
}) => {
  const isDraftMode = group === null;
  const api = useAxiosAuth0();
  const { getAccessTokenSilently, isAuthenticated, isLoading: authLoading } =
    useAuth0();
  const tenantId = useAppSelector((s) => s.selection.tenantId);
  const tenantName = useAppSelector((s) => s.selection.tenantName);
  const partnerName = useAppSelector((s) => s.selection.partnerName);

  /** Same rule as UsersPage / SideNav: partner-level scope when tenant === partner. */
  const scopeLabel =
    tenantName || partnerName || (tenantId ? `Tenant ${tenantId}` : null);

  const [persistedMembers, setPersistedMembers] = useState<GroupMember[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingRemovalIds, setPendingRemovalIds] = useState<Set<string>>(
    new Set()
  );

  const [pickerMode, setPickerMode] = useState<"users" | "email">("users");

  const [users, setUsers] = useState<User[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [pickedIds, setPickedIds] = useState<Set<string>>(new Set());

  /**
   * User list scope follows SideNav / UsersPage: `state.selection.tenantId`
   * only. Partners (Mith, VGSystem, …) and GLOBAL_ADMIN (CTX) switch scope via
   * the top partner + customer selectors — not an in-step dropdown.
   */
  useEffect(() => {
    if (readOnly) {
      setUsers([]);
      setUsersLoading(false);
      setUsersError(null);
      setPickedIds(new Set());
      return;
    }
    if (authLoading || !isAuthenticated) return;
    if (!tenantId) {
      setUsers([]);
      setUsersError(null);
      setPickedIds(new Set());
      return;
    }
    let cancelled = false;
    setUsersLoading(true);
    setUsersError(null);
    setPickedIds(new Set());

    void (async () => {
      try {
        const token = await getAccessTokenSilently();
        const list = await listPlatformUsersForTenant(token, tenantId);
        if (cancelled) return;
        setUsers(list);
      } catch {
        if (cancelled) return;
        setUsers([]);
        setUsersError("Couldn't load users for this scope.");
      } finally {
        if (!cancelled) setUsersLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [readOnly, authLoading, isAuthenticated, tenantId, getAccessTokenSilently]);

  const members = useMemo(
    () => (isDraftMode ? mapDraftToGroupMembers(draftMembers) : persistedMembers),
    [isDraftMode, draftMembers, persistedMembers]
  );

  const recipientsByTab = useMemo(() => {
    const map = new Map<string, GroupMember[]>();
    if (isDraftMode) {
      const mapped = mapDraftToGroupMembers(draftMembers);
      draftMembers.forEach((d, i) => {
        const key = recipientTabKeyFromDraftMember(d);
        const row = mapped[i];
        if (!map.has(key)) map.set(key, []);
        map.get(key)!.push(row);
      });
      return map;
    }
    for (const m of persistedMembers) {
      const key = recipientTabKeyPersisted(m);
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(m);
    }
    return map;
  }, [isDraftMode, draftMembers, persistedMembers]);

  const sortedRecipientTabKeys = useMemo(
    () => sortRecipientTabKeys(Array.from(recipientsByTab.keys())),
    [recipientsByTab]
  );

  const [activeRecipientsTab, setActiveRecipientsTab] = useState<string | null>(
    null
  );

  useEffect(() => {
    if (sortedRecipientTabKeys.length === 0) {
      setActiveRecipientsTab(null);
      return;
    }
    setActiveRecipientsTab((prev) => {
      if (prev && sortedRecipientTabKeys.includes(prev)) return prev;
      return sortedRecipientTabKeys[0];
    });
  }, [sortedRecipientTabKeys]);

  const activeRecipientRows = useMemo(() => {
    if (!activeRecipientsTab) return [];
    return recipientsByTab.get(activeRecipientsTab) ?? [];
  }, [activeRecipientsTab, recipientsByTab]);

  const recipientTabLabel = useCallback(
    (tabKey: string): string => {
      if (tabKey === RECIPIENTS_EXTERNAL_TAB) return "External email";
      const match = /^tenant:(\d+)$/.exec(tabKey);
      const cid = match ? Number(match[1]) : 0;
      if (cid === 0) return "Platform users";
      if (cid === tenantId && scopeLabel) return scopeLabel;
      return `Tenant ${cid}`;
    },
    [tenantId, scopeLabel]
  );

  const load = useCallback(async () => {
    if (!tenantId || !group) return;
    setLoading(true);
    try {
      const res = await listMembers(api, tenantId, group.id);
      setPersistedMembers(res.data.data);
      setError(null);
    } catch {
      setError("Failed to load recipients");
    } finally {
      setLoading(false);
    }
  }, [api, tenantId, group]);

  useEffect(() => {
    if (isDraftMode) return;
    void load();
  }, [isDraftMode, load]);

  const attachedAuth0Ids = useMemo(() => {
    const out = new Set<string>();
    for (const m of members) {
      if (m.auth0Id) out.add(m.auth0Id);
    }
    return out;
  }, [members]);

  const attachedEmails = useMemo(() => {
    const out = new Set<string>();
    for (const m of members) {
      if (m.email) out.add(m.email.toLowerCase());
    }
    return out;
  }, [members]);

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) =>
      [u.email, u.displayName ?? ""]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [users, search]);

  const togglePicked = useCallback((auth0Id: string) => {
    setPickedIds((prev) => {
      const next = new Set(prev);
      if (next.has(auth0Id)) next.delete(auth0Id);
      else next.add(auth0Id);
      return next;
    });
  }, []);

  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  const [emailInput, setEmailInput] = useState("");
  const [displayNameInput, setDisplayNameInput] = useState("");

  const handleAddUsers = async () => {
    if (readOnly) return;
    if (pickedIds.size === 0) return;
    if (!isDraftMode && (!tenantId || !group)) return;
    const payload: AddMemberInput[] = [];
    for (const auth0Id of Array.from(pickedIds)) {
      if (attachedAuth0Ids.has(auth0Id)) continue;
      const u = users.find((x) => x.auth0Id === auth0Id);
      if (!u) continue;
      const em = u.email?.toLowerCase();
      if (em && attachedEmails.has(em)) continue;
      payload.push({
        type: "user",
        auth0Id,
        email: u.email,
        displayName: u.displayName ?? undefined,
        pickerTenantId: tenantId || undefined,
      });
    }
    if (payload.length === 0) {
      setAddError("No new users to add.");
      return;
    }
    setAdding(true);
    setAddError(null);
    try {
      if (isDraftMode && onDraftMembersChange) {
        const seen = new Set(draftMembers.map(draftMemberKey));
        const next = [...draftMembers];
        for (const p of payload) {
          const k = draftMemberKey(p);
          if (!seen.has(k)) {
            seen.add(k);
            next.push(p);
          }
        }
        onDraftMembersChange(next);
        setPickedIds(new Set());
        return;
      }
      if (!tenantId || !group) return;
      await addMembers(api, tenantId, group.id, payload);
      setPickedIds(new Set());
      await load();
    } catch {
      setAddError("Failed to add recipients.");
    } finally {
      setAdding(false);
    }
  };

  const handleAddExternal = async () => {
    if (readOnly) return;
    const email = emailInput.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setAddError("Enter a valid email address");
      return;
    }
    if (attachedEmails.has(email)) {
      setAddError("That email is already in the group.");
      return;
    }
    setAdding(true);
    setAddError(null);
    try {
      const row: AddMemberInput = {
        type: "email",
        email,
        displayName: displayNameInput.trim() || undefined,
      };
      if (isDraftMode && onDraftMembersChange) {
        const k = draftMemberKey(row);
        if (draftMembers.some((m) => draftMemberKey(m) === k)) {
          setAddError("That email is already in the list.");
          return;
        }
        onDraftMembersChange([...draftMembers, row]);
        setEmailInput("");
        setDisplayNameInput("");
        return;
      }
      if (!tenantId || !group) return;
      await addMembers(api, tenantId, group.id, [row]);
      setEmailInput("");
      setDisplayNameInput("");
      await load();
    } catch {
      setAddError("Failed to add recipient.");
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = useCallback(
    async (memberId: string) => {
      if (readOnly) return;
      setError(null);
      if (isDraftMode && onDraftMembersChange) {
        const target = members.find((m) => m.id === memberId);
        if (!target) return;
        const key =
          target.memberType === "user" && target.auth0Id
            ? `user:${target.auth0Id}`
            : `email:${target.email.toLowerCase()}`;
        onDraftMembersChange(
          draftMembers.filter((m) => draftMemberKey(m) !== key)
        );
        return;
      }
      if (!tenantId || !group) return;
      setPendingRemovalIds((prev) => {
        const next = new Set(prev);
        next.add(memberId);
        return next;
      });
      try {
        await removeMember(api, tenantId, group.id, memberId);
        setPersistedMembers((prev) => prev.filter((m) => m.id !== memberId));
      } catch {
        setError("Failed to remove recipient");
      } finally {
        setPendingRemovalIds((prev) => {
          const next = new Set(prev);
          next.delete(memberId);
          return next;
        });
      }
    },
    [
      readOnly,
      isDraftMode,
      onDraftMembersChange,
      draftMembers,
      members,
      tenantId,
      group,
      api,
    ]
  );

  const recipientColumns = useMemo<GenericTableColumn<GroupMember>[]>(() => {
    const cols: GenericTableColumn<GroupMember>[] = [
      {
        id: "name",
        header: "Name",
        width: "38%",
        wrapMode: "truncate",
        renderCell: (row) => (
          <span className="text-sm text-semantic-text-primary block truncate">
            {row.displayLabel}
          </span>
        ),
      },
      {
        id: "email",
        header: "Email",
        wrapMode: "truncate",
        renderCell: (row) => (
          <span className="text-sm text-semantic-text-secondary block truncate">
            {row.email}
          </span>
        ),
      },
    ];
    if (!readOnly) {
      cols.push({
        id: "actions",
        header: "",
        width: "112px",
        fixed: "right",
        wrapMode: "nowrap",
        renderCell: (row) => {
          const isRemoving = pendingRemovalIds.has(row.id);
          return (
            <GenericButton
              label={isRemoving ? "Removing" : "Remove"}
              variant="danger"
              size="sm"
              loading={isRemoving}
              disabled={isRemoving}
              onClick={() => void handleRemove(row.id)}
            />
          );
        },
      });
    }
    return cols;
  }, [readOnly, pendingRemovalIds, handleRemove]);

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-md font-semibold text-semantic-text-primary">
          Recipients
        </h3>
        <p className="text-xs text-semantic-text-secondary mt-1 md:max-w-2xl">
          {readOnly ? (
            <>
              Recipients below come from the linked tag (read-only). Edit users
              on the tag in Tags Manager if you need to change them.
            </>
          ) : (
            <>
              Grant recipients by customer (same flow as Tags Manager), or add
              external emails without a platform account.
            </>
          )}
        </p>
        {readOnly && (
          <div className="mt-3 rounded-md border border-semantic-border-brand-secondary bg-semantic-bg-brand-secondary px-3 py-2 text-xs text-semantic-text-brand-primary">
            View-only: synced from Tags Manager until you leave this screen or
            change the linked tag on the Resources step.
          </div>
        )}
      </div>

      {error && (
        <p className="text-sm text-semantic-text-danger">{error}</p>
      )}

      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-semantic-text-secondary">
          {members.length === 0
            ? "Nobody on this group yet."
            : `${members.length} recipient${members.length === 1 ? "" : "s"} · ${
                sortedRecipientTabKeys.length
              } tab${sortedRecipientTabKeys.length === 1 ? "" : "s"}.`}
        </p>
      </div>

      {loading && members.length === 0 ? (
        <div className="space-y-2" aria-busy="true" aria-label="Loading recipients">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="flex items-center justify-between gap-3 px-3 py-2 border border-semantic-border-secondary rounded-md bg-semantic-bg-primary"
            >
              <div className="flex flex-col min-w-0 flex-1 gap-1.5">
                <span
                  className="h-4 w-44 rounded bg-semantic-bg-secondary animate-pulse"
                  aria-hidden
                />
                <span
                  className="h-3 w-56 rounded bg-semantic-bg-secondary animate-pulse"
                  aria-hidden
                />
              </div>
              <span
                className="h-8 w-20 rounded bg-semantic-bg-secondary animate-pulse"
                aria-hidden
              />
            </div>
          ))}
        </div>
      ) : members.length === 0 ? (
        <div className="text-center py-6 border border-dashed border-semantic-border-secondary rounded-md">
          <p className="text-sm text-semantic-text-secondary">
            No recipients on this group yet.
          </p>
        </div>
      ) : (
        <div>
          <h4 className="text-sm font-semibold text-semantic-text-primary mb-2">
            Selected recipients
          </h4>
          <div className="border border-semantic-border-secondary rounded-md overflow-hidden bg-semantic-bg-primary">
            <div className="flex items-stretch overflow-x-auto custom-scrollbar bg-semantic-bg-secondary border-b border-semantic-border-secondary">
              {sortedRecipientTabKeys.map((tabKey) => {
                const tabItems = recipientsByTab.get(tabKey) ?? [];
                const isActive = tabKey === activeRecipientsTab;
                return (
                  <button
                    key={tabKey}
                    type="button"
                    onClick={() => setActiveRecipientsTab(tabKey)}
                    className={`px-3 py-2 text-sm font-medium border-r border-semantic-border-secondary whitespace-nowrap ${
                      isActive
                        ? "bg-semantic-bg-primary border-b-2 border-b-semantic-border-brand-secondary -mb-px text-semantic-text-brand"
                        : "text-semantic-text-primary hover:bg-semantic-state-active"
                    }`}
                  >
                    {recipientTabLabel(tabKey)}
                    <span className="ml-1.5 text-xs text-semantic-text-secondary">
                      ({tabItems.length})
                    </span>
                  </button>
                );
              })}
            </div>

            {activeRecipientRows.length === 0 ? (
              <div className="px-3 py-6 text-sm text-semantic-text-secondary text-center">
                No recipients in this tab.
              </div>
            ) : (
              <GenericTable
                columns={recipientColumns}
                data={activeRecipientRows}
                rowKey={(row) => row.id}
                width="100%"
                maxTableBodyHeight={RECIPIENTS_TABLE_MAX_BODY_HEIGHT}
                isFooterVisible={false}
                tableClassName="border-0 shadow-none rounded-none"
              />
            )}
          </div>
        </div>
      )}

      {!readOnly && (
      <div className="border border-semantic-border-primary rounded-md p-4 bg-semantic-bg-secondary space-y-4">
        <div className="inline-flex items-center gap-1 rounded-sm bg-semantic-bg-secondary p-0.5 border border-semantic-border-secondary">
          <button
            type="button"
            disabled={readOnly}
            onClick={() => {
              setPickerMode("users");
              setAddError(null);
            }}
            className={`px-3 py-1 text-xs font-medium rounded-sm transition-colors disabled:opacity-50 ${
              pickerMode === "users"
                ? "bg-semantic-bg-primary text-semantic-text-primary shadow-sm"
                : "text-semantic-text-secondary"
            }`}
          >
            Platform users
          </button>
          <button
            type="button"
            disabled={readOnly}
            onClick={() => {
              setPickerMode("email");
              setAddError(null);
            }}
            className={`px-3 py-1 text-xs font-medium rounded-sm transition-colors ${
              pickerMode === "email"
                ? "bg-semantic-bg-primary text-semantic-text-primary shadow-sm"
                : "text-semantic-text-secondary"
            } disabled:opacity-50`}
          >
            External email
          </button>
        </div>

        {addError && (
          <div className="px-3 py-2 rounded-md text-sm bg-red-50 text-red-700 border border-red-200">
            {addError}
          </div>
        )}

        {pickerMode === "users" ? (
          <>
            <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-semantic-text-secondary">
                  Scope:
                </span>
                <span className="inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-medium bg-semantic-bg-brand-secondary text-semantic-text-brand whitespace-nowrap">
                  {scopeLabel ?? "—"}
                </span>
              </div>
              <p className="text-xs text-semantic-text-secondary md:text-right">
                Use the partner and customer selectors at the top to change
                scope. Pick users from <strong>{scopeLabel ?? "—"}</strong>.
              </p>
            </div>

            {!tenantId ? (
              <div className="text-center py-8 border border-dashed border-semantic-border-secondary rounded-md bg-semantic-bg-primary">
                <p className="text-sm text-semantic-text-secondary">
                  Select a partner or customer in the top navigation to load
                  users.
                </p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-end">
                  <GenericInput
                    label="Filter users"
                    value={search}
                    onChange={setSearch}
                    placeholder="Search by name or email…"
                    type="search"
                  />
                  <GenericButton
                    label={
                      pickedIds.size === 0
                        ? "Add"
                        : `Add ${pickedIds.size} user${
                            pickedIds.size === 1 ? "" : "s"
                          }`
                    }
                    variant="brand"
                    size="md"
                    loading={adding}
                    disabled={
                      readOnly ||
                      pickedIds.size === 0 ||
                      adding ||
                      usersLoading
                    }
                    onClick={() => void handleAddUsers()}
                  />
                </div>

                <div className="border border-semantic-border-secondary rounded-md overflow-hidden bg-semantic-bg-primary">
                  <div className="grid grid-cols-[36px_1fr_1fr] gap-3 px-3 py-2 text-xs font-semibold text-semantic-text-secondary uppercase tracking-wide bg-semantic-bg-secondary border-b border-semantic-border-secondary">
                    <span></span>
                    <span>Name</span>
                    <span>Email</span>
                  </div>
                  <div className="max-h-[220px] overflow-y-auto custom-scrollbar divide-y divide-semantic-border-secondary">
                    {usersLoading ? (
                      <div className="flex items-center justify-center py-12">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-semantic-bg-brand-bold-secondary" />
                      </div>
                    ) : usersError ? (
                      <div className="px-3 py-6 text-sm text-semantic-text-danger">
                        {usersError}
                      </div>
                    ) : filteredUsers.length === 0 ? (
                      <div className="px-3 py-8 text-sm text-semantic-text-secondary text-center">
                        {users.length === 0
                          ? "No users for this customer."
                          : "No matches for the current filter."}
                      </div>
                    ) : (
                      filteredUsers.map((u) => {
                        const isAttached: boolean =
                          attachedAuth0Ids.has(u.auth0Id) ||
                          (!!u.email &&
                            attachedEmails.has(u.email.toLowerCase()));
                        const isPicked = pickedIds.has(u.auth0Id);
                        return (
                          <button
                            key={u.auth0Id}
                            type="button"
                            onClick={() =>
                              !readOnly &&
                              !isAttached &&
                              togglePicked(u.auth0Id)
                            }
                            disabled={readOnly || isAttached}
                            className={`w-full grid grid-cols-[36px_1fr_1fr] gap-3 px-3 py-2 text-sm text-left items-center transition-colors ${
                              isAttached
                                ? "opacity-50 cursor-not-allowed"
                                : isPicked
                                ? "bg-semantic-state-active"
                                : "hover:bg-semantic-state-active"
                            }`}
                          >
                            <span className="flex items-center">
                              <input
                                type="checkbox"
                                checked={isPicked || isAttached}
                                readOnly
                                disabled={isAttached}
                                className="h-4 w-4 cursor-pointer accent-semantic-bg-brand-bold-secondary"
                              />
                            </span>
                            <span className="text-semantic-text-primary truncate">
                              {u.displayName || u.email}
                              {isAttached && (
                                <span className="ml-2 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-sm bg-semantic-bg-secondary text-semantic-text-secondary">
                                  Already in group
                                </span>
                              )}
                            </span>
                            <span className="text-semantic-text-secondary truncate">
                              {u.email}
                            </span>
                          </button>
                        );
                      })
                    )}
                  </div>
                </div>
              </>
            )}
          </>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <GenericInput
                label="Email"
                value={emailInput}
                onChange={setEmailInput}
                placeholder="noc@partner.com"
                type="email"
              />
              <GenericInput
                label="Display name (optional)"
                value={displayNameInput}
                onChange={setDisplayNameInput}
                placeholder='e.g. "Partner NOC"'
              />
            </div>
            <p className="text-xs text-semantic-text-secondary">
              For recipients without a platform account — partner NOCs, personal
              emails, distribution lists, etc.
            </p>
            <div className="flex justify-end">
              <GenericButton
                label="Add email"
                variant="brand"
                size="md"
                loading={adding}
                disabled={readOnly || adding || !emailInput.trim()}
                onClick={() => void handleAddExternal()}
              />
            </div>
          </div>
        )}
      </div>
      )}
    </div>
  );
};

export default GroupMembersSection;
